import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnDestroy,
  SimpleChanges,
  inject,
  input,
  AfterViewInit,
  ChangeDetectorRef,
  ElementRef,
  OnChanges,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  ApexAxisChartSeries,
  ApexXAxis,
  NgApexchartsModule,
} from 'ng-apexcharts';
import { NoxDatePickerComponent } from '../nox-date-picker/nox-date-picker.component';
import {
  NoxDropdown,
  TrendDropdownKey,
  getTrendsDropDown,
} from '../../models/dropdown';
import { ChartOptions, NoxChartDta, NoxChartXY } from '../../models/nox-chart';
import { TrendSelection, TrendType } from '../../models/enum';
import { DateUtils, NoxMonth, NoxYear } from '../../utils/date_utils';
import { ProductService } from '../../service/product.service';
import { ClickOutsideDirective } from '../../utils/click.outside.directive';
import { BaseComponent } from '../../../base/components/base.component';
import { Subject, takeUntil } from 'rxjs';
import {
  INVERTER_TYPE,
  PLANT_TYPE,
  USER_KEY,
} from '../../service/storage.service';
import {
  InverterType,
  MappingProtocol,
  PlantTypeEnum,
} from '../../models/stats';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { CspNonceService } from '../../service/csp-nonce.service';

@Component({
  selector: 'app-trends',
  imports: [
    RouterModule,
    CommonModule,
    TranslateModule,
    NgApexchartsModule,
    NoxDatePickerComponent,
    ClickOutsideDirective,
    NgxSpinnerModule,
  ],
  templateUrl: './trends.component.html',
  styleUrl: './trends.component.scss',
})
export class TrendsComponent
  extends BaseComponent
  implements OnDestroy, AfterViewInit, OnChanges
{
  yAxisTitle!: string;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef);
  private readonly chartElementsWithListeners = new Set<Element>();
  private static originalAddEventListener: any;
  private static patchCount = 0;
  private eventListenerPatched = false;

  enableNextDate(selectedDate: Date, todayDate: Date) {
    let nextDay = new Date(selectedDate);
    nextDay.setDate(selectedDate.getDate() + 1);
    return nextDay < todayDate;
  }

  enableTrendDropdown = false;
  trendDropdown: NoxDropdown[] = getTrendsDropDown();
  selectedTrend: NoxDropdown = this.trendDropdown[0];
  series: ApexAxisChartSeries = [];
  chartOptions: Partial<ChartOptions> = {
    legend: { show: false },
  };

  trendSelection = TrendSelection.DAY;
  trendType = TrendType.POWER_GENERATION;
  noxMonthsData = NoxMonth.noxMonthsData();

  prodService = inject(ProductService);
  dropdownVisible = false;
  yearDropdownVisible = false;
  notificationDropdownVisible = false;
  yearList = NoxYear.yearList();
  selectedDate = new Date();
  todayDate = new Date();

  noxChartData: NoxChartDta[] = [];
  noxChartXY: NoxChartXY[] = [];
  ngUnsubscribe: Subject<void> = new Subject<void>();
  spinnerTag: string = 'trendsSpinner';
  @Input() plantId?: string;
  @Input() plantType?: string;
  @Input() mappingProtocol?: MappingProtocol;
  @Input() categoryTitle?: string;
  inverterType: string | null | undefined;
  siId: string;
  categoryId = input<string>();
  trendSpinner: NgxSpinnerService = inject(NgxSpinnerService);
  spinnerShowing: boolean = false;
  displayDate: any;
  minYear = new Date().getFullYear() - 1;
  currentYear = new Date().getFullYear();
  currentMonth = new Date().getMonth() + 1;
  constructor(private readonly cspNonceService: CspNonceService) {
    super();
    let user = this.storageService.getObject(USER_KEY);
    this.siId = user.installer_id;
  }

  ngAfterViewInit(): void {
    this.patchEventListeners();
    if (this.categoryId()) {
      this.trendDropdown = getTrendsDropDown(this.categoryId());
      this.selectedTrend = this.trendDropdown[0];
      setTimeout(() => {
        if (this.categoryId()) {
          let todayTimeStamp = DateUtils.getStartDayTimeStampFromDate(
            this.selectedDate,
          );
          this.trendDropdown = getTrendsDropDown(this.categoryId());
          this.selectedTrend = this.trendDropdown[0];

          this.trendSelection = TrendSelection.DAY;
          if (this.categoryId() == '2') {
            this.trendType = TrendType.POWER_CONSUMPTION;
            this.fetchPowerConsumptionTrends(
              this.siId,
              todayTimeStamp,
              null,
              null,
              null,
              this.categoryId(),
            );
          } else {
            this.trendType = TrendType.POWER_GENERATION;
            this.fetchPowerGenerationTrends(
              this.siId,
              todayTimeStamp,
              null,
              null,
              null,
              this.categoryId(),
            );
          }
        }
      }, 0);
    }
  }

  private patchEventListeners(): void {
    if (!this.eventListenerPatched && typeof EventTarget !== 'undefined') {
      if (TrendsComponent.patchCount === 0) {
        TrendsComponent.originalAddEventListener =
          EventTarget.prototype.addEventListener;

        EventTarget.prototype.addEventListener = function (
          this: EventTarget,
          type: string,
          listener: EventListenerOrEventListenerObject,
          options?: boolean | AddEventListenerOptions,
        ) {
          if (
            ['wheel', 'mousewheel', 'touchstart', 'touchmove'].includes(type)
          ) {
            if (typeof options === 'object') {
              options = { ...options, passive: false };
            } else {
              options = { passive: false, capture: !!options };
            }
          }
          return TrendsComponent.originalAddEventListener.call(
            this,
            type,
            listener,
            options,
          );
        };
      }

      TrendsComponent.patchCount++;
      this.eventListenerPatched = true;
    }
  }

  private restoreEventListeners(): void {
    // Restore original addEventListener when last component instance is destroyed
    if (this.eventListenerPatched) {
      TrendsComponent.patchCount--;

      if (
        TrendsComponent.patchCount === 0 &&
        TrendsComponent.originalAddEventListener
      ) {
        EventTarget.prototype.addEventListener =
          TrendsComponent.originalAddEventListener;
      }

      this.eventListenerPatched = false;
    }
  }

  private registerNonPassiveListeners(): void {
    setTimeout(() => {
      const chartContainer =
        this.elementRef.nativeElement.querySelector('apx-chart');
      if (
        chartContainer &&
        !this.chartElementsWithListeners.has(chartContainer)
      ) {
        const events = ['wheel', 'mousewheel', 'touchstart', 'touchmove'];

        events.forEach((eventName) => {
          chartContainer.addEventListener(eventName, this.handleChartEvent, {
            passive: false,
            capture: true,
          });
        });

        this.chartElementsWithListeners.add(chartContainer);
      }
    }, 150);
  }

  private readonly handleChartEvent = (e: Event): void => {
    // Empty handler with passive: false allows child elements to preventDefault
  };

  private cleanupEventListeners(): void {
    const events = ['wheel', 'mousewheel', 'touchstart', 'touchmove'];
    this.chartElementsWithListeners.forEach((element) => {
      events.forEach((eventName) => {
        (element as HTMLElement).removeEventListener(
          eventName,
          this.handleChartEvent,
          { capture: true } as any,
        );
      });
    });
    this.chartElementsWithListeners.clear();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['plantId']?.currentValue &&
      changes['plantId'].currentValue.toString().length > 0
    ) {
      let plantType = this.storageService.get(PLANT_TYPE);
      this.inverterType = this.storageService.get(INVERTER_TYPE);
      this.selectedDate = new Date();
      this.todayDate = new Date();
      let todayTimeStamp = DateUtils.getStartDayTimeStampFromDate(
        this.selectedDate,
      );
      this.trendSelection = TrendSelection.DAY;

      if (plantType == PlantTypeEnum.NON_SOLAR) {
        this.trendDropdown = getTrendsDropDown('2', this.mappingProtocol);
        this.selectedTrend = this.trendDropdown[0];
        if (this.selectedTrend.key === TrendDropdownKey.powerCut) {
          this.trendType = TrendType.POWER_CUT;
          this.fetchPowerCutTrends(
            this.plantId!, //'75d54eea-235b-4880-954e-ea235b8880e2',
            todayTimeStamp, //1706725800,
            null,
            null,
            null,
          );
        } else {
          this.trendType = TrendType.POWER_CONSUMPTION;
          this.fetchPowerConsumptionTrends(
            this.plantId!, //'75d54eea-235b-4880-954e-ea235b8880e2',
            todayTimeStamp, //1706725800,
            null,
            null,
            null,
          );
        }
      } else {
        this.selectedDate = new Date();
        this.todayDate = new Date();
        this.trendDropdown = getTrendsDropDown('1', this.mappingProtocol);
        this.selectedTrend = this.trendDropdown[0];
        this.trendType = TrendType.POWER_GENERATION;
        this.fetchPowerGenerationTrends(
          this.plantId!, //'75d54eea-235b-4880-954e-ea235b8880e2',
          todayTimeStamp, //1706725800,
          null,
          null,
          null,
        );
        if (
          this.inverterType === InverterType.GTI ||
          this.inverterType === InverterType.ThinkPower
        ) {
          this.trendDropdown = this.trendDropdown.filter(
            (item: NoxDropdown) => item.key !== TrendDropdownKey.consumption,
          );
        }
      }
    }
  }

  /**
   *
   * Update trends base on date,month and year
   * @param date
   * @param month
   * @param year
   */
  updateTrends(date: Date | null, month: number | null, year: number | null) {
    let startDate = null;
    let todayTimeStamp = null;
    let endDate = null;
    if (date != null) {
      this.trendSelection = TrendSelection.DAY;
      this.selectedDate = date;
      todayTimeStamp = DateUtils.getStartDayTimeStampFromDate(date);
    } else if (year != null) {
      this.trendSelection = TrendSelection.YEARLY;
      this.selectedDate = new Date(year, 0, 1);
      const firstDay = DateUtils.getFirstDay(year);
      const lastDay = DateUtils.getLastDay(year);
      startDate = DateUtils.getStartDayTimeStampFromDate(firstDay);
      endDate = DateUtils.getStartDayTimeStampFromDate(lastDay);
    } else if (month != null) {
      this.trendSelection = TrendSelection.MONTHLY;
      const selectedYear = this.noxMonthsData.year;
      this.selectedDate = new Date(selectedYear, month - 1, 1);
      const firstDay = DateUtils.getFirstDay(selectedYear, month);
      const lastDay = DateUtils.getLastDay(selectedYear, month);
      startDate = DateUtils.getStartDayTimeStampFromDate(firstDay);
      endDate = DateUtils.getStartDayTimeStampFromDate(lastDay);
    }
    if (this.plantId || this.siId) {
      this.trendSpinner.show(this.spinnerTag);
      this.spinnerShowing = true;
      this.cdr.detectChanges();

      switch (this.trendType) {
        case TrendType.POWER_GENERATION:
          this.fetchPowerGenerationTrends(
            this.plantId ?? this.siId,
            todayTimeStamp,
            startDate,
            endDate,
            year,
            this.categoryId(),
          );
          break;
        case TrendType.POWER_CONSUMPTION:
          this.fetchPowerConsumptionTrends(
            this.plantId ?? this.siId,
            todayTimeStamp,
            startDate,
            endDate,
            year,
            this.categoryId(),
          );
          break;
        case TrendType.POWER_CUT:
          this.fetchPowerCutTrends(
            this.plantId ?? this.siId,
            todayTimeStamp,
            startDate,
            endDate,
            year,
            this.categoryId(),
          );
          break;
      }
    }
  }

  /**
   * switch trend between generation,consumption and power cut
   * @param noxDropdown
   */
  switchTrends(noxDropdown: NoxDropdown) {
    this.trendSelection = TrendSelection.DAY;
    this.selectedDate = new Date();
    this.selectedTrend = noxDropdown;
    let todayTimeStamp = DateUtils.getStartDayTimeStampFromDate(
      this.selectedDate,
    );
    if (this.plantId || this.siId) {
      switch (noxDropdown.key) {
        case TrendDropdownKey.consumption:
          this.trendType = TrendType.POWER_CONSUMPTION;
          this.fetchPowerConsumptionTrends(
            this.plantId ?? this.siId, //'d52bd09b-e0e6-4241-abd0-9be0e64241b0',
            todayTimeStamp,
            null,
            null,
            null,
            this.categoryId(),
          );

          break;
        case TrendDropdownKey.powerCut:
          this.trendType = TrendType.POWER_CUT;
          this.fetchPowerCutTrends(
            this.plantId ?? this.siId, //'d52bd09b-e0e6-4241-abd0-9be0e64241b0',
            todayTimeStamp,
            null,
            null,
            null,
            this.categoryId(),
          );
          break;
        case TrendDropdownKey.generation:
          this.trendType = TrendType.POWER_GENERATION;
          this.fetchPowerGenerationTrends(
            this.plantId ?? this.siId, //'75d54eea-235b-4880-954e-ea235b8880e2',
            todayTimeStamp,
            null,
            null,
            null,
            this.categoryId(),
          );
          break;
      }
    }
  }
  fetchPowerGenerationTrends(
    id: string,
    date: number | null,
    startDate: number | null,
    endDate: number | null,
    year: number | null,
    type?: string | undefined,
  ) {
    let getPowerGenerationTrends = new Subject<boolean>();
    this.clearGraphData();
    this.prodService
      .getPowerGenerationTrends(id, date, startDate, endDate, year, type)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (trendsResponse) => {
          this.clearGraphData();
          if (trendsResponse) {
            this.noxChartXY = [];
            this.noxChartData = [];
            let powerGenerationData = trendsResponse.data;
            if (Object.keys(powerGenerationData).length > 0) {
              switch (this.trendSelection) {
                case TrendSelection.DAY:
                  if (
                    powerGenerationData.instantiateGeneration &&
                    powerGenerationData.instantiateGeneration.length > 0
                  ) {
                    powerGenerationData.instantiateGeneration.sort(
                      (first, second) => first.time - second.time,
                    );
                    powerGenerationData.instantiateGeneration.forEach(
                      (item) => {
                        this.noxChartXY.push({
                          x: item.time * 1000, // convert to milliseconds
                          y: Number(item.generation || 0),
                        });
                      },
                    );
                    this.noxChartData.push({
                      name: "Today's  Power Generation",
                      noxXY: this.noxChartXY,
                    });
                    this.plotLineChart(this.noxChartData);
                  }

                  break;
                case TrendSelection.CUSTOM:

                case TrendSelection.MONTHLY:
                  if (
                    powerGenerationData.generations &&
                    powerGenerationData.generations.length > 0
                  ) {
                    powerGenerationData.generations.sort(
                      (first, second) => first.date - second.date,
                    );
                    powerGenerationData.generations.forEach((item) => {
                      this.noxChartXY.push({
                        x: item.date * 1000, // convert to milliseconds
                        y: Number(Number(item.generation).toFixed(3)),
                      });
                    });
                    this.noxChartData.push({
                      name: 'Monthly Power Generation',
                      noxXY: this.noxChartXY,
                    });
                    this.plotBarChart(this.noxChartData);
                  }

                  break;
                case TrendSelection.YEARLY:
                  if (
                    powerGenerationData.generations &&
                    powerGenerationData.generations.length > 0
                  ) {
                    powerGenerationData.generations.sort(
                      (first, second) => first.date - second.date,
                    );
                    powerGenerationData.generations.forEach((item) => {
                      if (item.month) {
                        this.noxChartXY.push({
                          x: item.month, // convert to milliseconds
                          y: Number(item.generation),
                        });
                      }
                    });
                    this.noxChartData.push({
                      name: 'Yearly Power Generation',
                      noxXY: this.noxChartXY,
                    });
                    this.plotBarChart(this.noxChartData);
                  }

                  break;
              }
            }
            getPowerGenerationTrends.next(true);
          }
          this.hideSpinner();
        },
        error: (err) => {
          getPowerGenerationTrends.next(true);
          this.logger.error(err);
          this.hideSpinner();
        },
        complete: () => {
          this.hideSpinner();
          this.baseUserService.trendsLoading.next(true);
          getPowerGenerationTrends.complete();
        },
      });
  }
  hideSpinner() {
    this.trendSpinner.hide(this.spinnerTag);
    this.spinnerShowing = false;
    this.cdr.detectChanges();
  }
  showSpinner() {
    this.trendSpinner.show(this.spinnerTag);
    this.spinnerShowing = true;
    this.cdr.detectChanges();
  }

  fetchPowerConsumptionTrends(
    plantId: string,
    date: number | null,
    startDate: number | null,
    endDate: number | null,
    year: number | null,
    type?: string,
  ) {
    this.clearGraphData();
    this.prodService
      .getPowerConsumptionTrends(plantId, date, startDate, endDate, year, type)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (powerConsumptionResponse) => {
          this.clearGraphData();
          let powerConsumptionData = powerConsumptionResponse?.data;
          if (
            powerConsumptionData &&
            Object.keys(powerConsumptionData).length > 0
          ) {
            switch (this.trendSelection) {
              case TrendSelection.DAY:
                if (
                  powerConsumptionData.instantiateConsumption &&
                  powerConsumptionData.instantiateConsumption.length > 0
                ) {
                  powerConsumptionData.instantiateConsumption.sort(
                    (first, second) => first.time - second.time,
                  );
                  powerConsumptionData.instantiateConsumption.forEach(
                    (item) => {
                      this.noxChartXY.push({
                        x: item.time * 1000, // convert to milliseconds
                        y: Number(Number(item.consumption).toFixed(3)),
                      });
                    },
                  );
                  this.noxChartData.push({
                    name: "Today's Power Consumption",
                    noxXY: this.noxChartXY,
                  });
                  this.plotLineChart(this.noxChartData);
                }

                break;
              case TrendSelection.CUSTOM:

              case TrendSelection.MONTHLY:
                if (
                  powerConsumptionData.consumptions &&
                  powerConsumptionData.consumptions.length > 0
                ) {
                  powerConsumptionData.consumptions.sort(
                    (first, second) => first.date - second.date,
                  );
                  powerConsumptionData.consumptions.forEach((item) => {
                    this.noxChartXY.push({
                      x: item.date * 1000, // convert to milliseconds
                      y: Number(Number(item.consumption).toFixed(3)),
                    });
                  });
                  this.noxChartData.push({
                    name: 'Monthly Power Consumption',
                    noxXY: this.noxChartXY,
                  });
                  this.plotBarChart(this.noxChartData);
                }

                break;
              case TrendSelection.YEARLY:
                if (
                  powerConsumptionData.consumptions &&
                  powerConsumptionData.consumptions.length > 0
                ) {
                  powerConsumptionData.consumptions.sort(
                    (first, second) => first.month - second.month,
                  );
                  powerConsumptionData.consumptions.forEach((item) => {
                    this.noxChartXY.push({
                      x: item.month,
                      y: Number(item.consumption),
                    });
                  });
                  this.noxChartData.push({
                    name: 'Yearly Power Consumption',
                    noxXY: this.noxChartXY,
                  });
                  this.plotBarChart(this.noxChartData);
                }

                break;
            }
          }
          this.hideSpinner();
        },
        error: (err) => {
          this.logger.error(err);
          this.hideSpinner();
        },
        complete: () => {
          this.hideSpinner();
          this.baseUserService.trendsLoading.next(true);
        },
      });
  }
  clearGraphData() {
    this.series = [];
    this.chartOptions = {
      series: [],
      chart: undefined as any,
      xaxis: undefined as any,
      yaxis: undefined as any,
      dataLabels: { enabled: false },
      stroke: { width: 0 },
      markers: { size: 0 },
      grid: {},
      plotOptions: {},
      fill: {},
      tooltip: {},
      legend: { show: false },
    };
    this.noxChartXY = [];
    this.noxChartData = [];
    this.cdr.detectChanges();
  }

  fetchPowerCutTrends(
    id: string,
    date: number | null,
    startDate: number | null,
    endDate: number | null,
    year: number | null,
    type?: string,
  ) {
    this.clearGraphData();
    this.prodService
      .getPowerCutGeneration(id, date, startDate, endDate, year, type)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (powerCutResponse) => {
          this.clearGraphData();
          let powerCutData = powerCutResponse.data;

          if (powerCutData && Object.keys(powerCutData).length > 0) {
            switch (this.trendSelection) {
              case TrendSelection.DAY:
                powerCutData.sort((first, second) => first.hour - second.hour);
                powerCutData.forEach((item) => {
                  let timestamp = DateUtils.getTimestampsForHours(item.hour);

                  this.noxChartXY.push({
                    x: timestamp, // convert to milliseconds
                    y: Number(item.minutes),
                  });
                });
                this.noxChartData.push({
                  name: "Today's  Power Cut",
                  noxXY: this.noxChartXY,
                });
                this.plotBarChart(this.noxChartData);
                break;
              case TrendSelection.CUSTOM:

              case TrendSelection.MONTHLY:
                powerCutData.sort((first, second) => first.date - second.date);
                powerCutData.forEach((item) => {
                  this.noxChartXY.push({
                    x: item.date * 1000, // convert to milliseconds
                    y: Number(item.minutes),
                  });
                });
                this.noxChartData.push({
                  name: 'Monthly Power Cut',
                  noxXY: this.noxChartXY,
                });
                this.plotBarChart(this.noxChartData);
                break;
              case TrendSelection.YEARLY:
                powerCutData.sort(
                  (first, second) => first.month - second.month,
                );
                powerCutData.forEach((item) => {
                  this.noxChartXY.push({
                    x: item.month,
                    y: Number(item.hours + '.' + item.minutes),
                  });
                });
                this.noxChartData.push({
                  name: 'Yearly Power Cut',
                  noxXY: this.noxChartXY,
                });
                this.plotBarChart(this.noxChartData);
                break;
            }
          }
          this.hideSpinner();
        },
        error: (err) => {
          this.logger.error(err);
          this.hideSpinner();
        },
        complete: () => {
          this.hideSpinner();
          this.baseUserService.trendsLoading.next(true);
        },
      });
  }

  toggleDropdown() {
    if (this.isMonthYearDisabled()) return;
    this.dropdownVisible = !this.dropdownVisible;
  }
  clickOutside(selection: string) {
    if (selection === 'YEARLY') {
      this.yearDropdownVisible = false;
    } else if (selection === 'MONTHLY') {
      this.dropdownVisible = false;
    } else if (selection === 'NOTIFICATION') {
      this.notificationDropdownVisible = false;
    } else if (selection === 'TREND_TYPE') {
      this.enableTrendDropdown = false;
    }
  }
  toggleYearDropdown() {
    if (this.isMonthYearDisabled()) return;
    this.yearDropdownVisible = !this.yearDropdownVisible;
  }
  prevDate() {
    this.trendSelection = TrendSelection.DAY;
    this.selectedDate = new Date(
      this.selectedDate.setDate(this.selectedDate.getDate() - 1),
    );

    this.updateTrends(this.selectedDate, null, null);
  }
  nextDate() {
    this.trendSelection = TrendSelection.DAY;
    this.selectedDate = new Date(
      this.selectedDate.setDate(this.selectedDate.getDate() + 1),
    );
    this.updateTrends(this.selectedDate, null, null);
  }
  onDateSelected($date: Date) {
    this.trendSelection = TrendSelection.DAY;
    this.selectedDate = $date;
    this.displayDate = this.selectedDate;
    this.updateTrends($date, null, null);
  }

  /**
   * Plot bar chart
   * @param data
   */
  plotBarChart(data: NoxChartDta[]) {
    // Safety check: ensure data is valid
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.warn('plotBarChart: Invalid or empty data');
      this.clearGraphData();
      return;
    }

    // Filter out any invalid items
    const validData = data.filter(
      (item) =>
        item?.name && Array.isArray(item.noxXY) && item.noxXY.length > 0,
    );

    if (validData.length === 0) {
      console.warn('plotBarChart: No valid data items');
      this.clearGraphData();
      return;
    }

    switch (this.trendType) {
      case TrendType.POWER_CUT:
        this.series = validData.map((item) => {
          return {
            name: item.name,
            data: item.noxXY.map((item) => {
              let xData;

              switch (this.trendSelection) {
                case TrendSelection.DAY:
                  xData = DateUtils.formatDateFromLocalEpoch(item.x, 'hh a');
                  break;
                case TrendSelection.MONTHLY:
                  xData = DateUtils.formatDateFromLocalEpoch(item.x, 'DD/MM');
                  break;
                case TrendSelection.YEARLY:
                  xData = DateUtils.getMonthName(item.x);
                  break;
                default:
                  xData = DateUtils.formatDateFromLocalEpoch(item.x, 'hh a');
                  break;
              }
              return {
                x: xData,
                y: item.y.toFixed(3),
              };
            }),
          };
        });
        break;
      default:
        this.series =
          this.trendSelection === TrendSelection.YEARLY
            ? validData.map((item) => {
                return {
                  name: item.name,
                  data: item.noxXY.map((item) => {
                    let xData;
                    xData = DateUtils.getMonthName(item.x);
                    return {
                      x: xData,
                      y: item.y.toFixed(3),
                    };
                  }),
                };
              })
            : validData.map((item) => {
                return {
                  name: item.name,
                  data: item.noxXY.map((item) => {
                    let xData;
                    xData = DateUtils.formatDateFromLocalEpoch(item.x, 'DD/MM');
                    return {
                      x: xData,
                      y: item.y.toFixed(3),
                    };
                  }),
                };
              });
        break;
    }

    /**
     * Default xAxisValue detail
     */
    let xAxisValue: ApexXAxis = {
      type: 'datetime',
      tickAmount: 8,
      min: validData[0].noxXY[0].x,
      max: validData[0].noxXY.at(-1)?.x,
      labels: {
        datetimeUTC: false,
        formatter: function (value, timestamp: number) {
          return DateUtils.formatDateFromLocalEpoch(timestamp);
        },
        style: {
          colors: '#94989E',
          fontSize: '12 ',
          fontWeight: 500,
        },
      },

      title: {
        offsetY: 10,
        text: this.getTitle(),
        style: {
          color: '#FFFFFF',
          fontSize: '12',
          fontFamily: 'Roboto, sans-serif',
          fontWeight: 500,
        },
      },
    };

    /**
     * For power cut trend type
     */
    if (this.trendType === TrendType.POWER_CUT) {
      xAxisValue = {
        type: 'category',
        categories: data[0].noxXY.map((item) => {
          let data;
          switch (this.trendSelection) {
            case TrendSelection.DAY:
              data = DateUtils.formatDateFromLocalEpoch(item.x, 'hh a');

              break;
            case TrendSelection.MONTHLY:
              data = DateUtils.formatDateFromLocalEpoch(item.x, 'DD/MM');
              break;
            case TrendSelection.YEARLY:
              data = DateUtils.getMonthName(item.x);

              break;
            default:
              data = DateUtils.formatDateFromLocalEpoch(item.x, 'hh a');
          }
          return data;
        }),
        labels: {
          showDuplicates: false,
          style: {
            colors: '#94989E',
            fontSize: '10',
            fontWeight: '500',
          },
        },
        title: {
          text: this.getTitle(),

          style: {
            color: '#FFFFFF',
            fontSize: '12',
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 400,
          },
        },
      };
      this.yAxisTitle = 'mins';
    } else {
      /**
       * For other trend type
       */
      if (
        this.trendSelection === TrendSelection.MONTHLY ||
        this.trendSelection === TrendSelection.CUSTOM
      ) {
        xAxisValue = {
          categories: data[0].noxXY.map((item) =>
            DateUtils.formatDateFromLocalEpoch(item.x, 'DD/MM'),
          ),
          labels: {
            showDuplicates: false,
            datetimeUTC: false,
            format: 'dd/MM',
            style: {
              colors: '#94989E',
              fontSize: '10',
            },
          },
        };
        this.yAxisTitle = 'kWh';
      } else if (this.trendSelection === TrendSelection.YEARLY) {
        xAxisValue = {
          tickAmount: 8,
          categories: data[0].noxXY.map((item) =>
            DateUtils.getMonthName(item.x),
          ),
          labels: {
            showDuplicates: false,
            datetimeUTC: false,
            style: {
              colors: '#94989E',
              fontSize: '10',
            },
          },
          title: {
            offsetY: 10,
            text: this.getTitle(),
            style: {
              color: '#FFFFFF',
              fontSize: '12',
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 400,
            },
          },
        };
        this.yAxisTitle = 'kWh';
      }
    }

    const nonce = this.cspNonceService.getNonce() ?? undefined;

    this.chartOptions = {
      series: this.series,
      chart: {
        type: 'bar',
        nonce: nonce,
        height: '96%',
        offsetY: 0,
        toolbar: {
          show: true,
          tools: {
            pan: true,
          },
          autoSelected: 'pan',
        },

        events: {
          beforeZoom: (e, { xaxis }) => {
            const noxXY = validData[0].noxXY;
            const first = noxXY.at(0);
            const last = noxXY.at(-1);
            if (!first || !last) {
              return; // or handle empty data case safely
            }
            const mainDifference =
              new Date(last.x).valueOf() - new Date(first.x).valueOf();
            const zoomDifference = xaxis.max - xaxis.min;
            if (zoomDifference > mainDifference) {
              return {
                xaxis: {
                  min: first.x,
                  max: last.x,
                },
              };
            }

            return {
              xaxis: {
                min: xaxis.min,

                max: xaxis.max,
              },
            };
          },
        },
      },
      grid: {
        padding: {
          bottom: 5,
        },
      },
      fill: {
        type: 'solid ',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          colorStops: [
            {
              offset: 0,
              color: '#527563',
              opacity: 1,
            },
            {
              offset: 20,
              color: '#0576c1',
              opacity: 1,
            },
          ],
        },
      },
      
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: this.getColumnWidth(),
          borderRadius: 5,
          borderRadiusApplication: 'end',
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: 0,
      },
      markers: {
        size: 0,
      },
      legend: {
        show: false,
      },
      yaxis: {
        min:0,
        title: {
          text: this.updateYAxisLabel(),
          style: {
            color: '#FFFFFF',
            fontSize: '12',
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 500,
          },
        },
        labels: {
          style: {
            colors: '#94989E',
            fontSize: '12',
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 500,
          },
        },
      },
      xaxis: xAxisValue,
      tooltip: {
        x: {
          show: true,
        },
        y: {
          title: {
            formatter: () => ``,
          },
          formatter: (value: number) => {
            if (value == null) {
              return '-';
            }
            if (this.trendType !== TrendType.POWER_CUT) {
              return `${value} ${this.yAxisTitle}`;
            }
            const [hrs, mins] = value.toString().split('.');
            return mins ? `${hrs} hrs ${mins} mins` : `${hrs} mins`;
          },
        },
      },
    };
    this.cdr.detectChanges();
    this.registerNonPassiveListeners();
  }

  getColumnWidth(): string {
  const length = this.series?.[0]?.data?.length || 0;
  
  if(this.categoryId()){
  if (length >= 20) return '40%';
  }   else{
  if (length >= 20) return '40%';
  if(length<=20 && length>=12) return '42%';
  }
  return '20';
}
  updateYAxisLabel(): string {
    switch (this.trendType) {
      case TrendType.POWER_GENERATION:
        return this.trendSelection === TrendSelection.DAY
          ? 'Solar Power (kW)'
          : 'Solar Power (kWh)';

      case TrendType.POWER_CONSUMPTION:
        return this.trendSelection === TrendSelection.DAY
          ? 'Power Consumption (kW)'
          : 'Power Consumption (kWh)';

      case TrendType.POWER_CUT:
        return this.trendSelection === TrendSelection.DAY ||
          this.trendSelection === TrendSelection.MONTHLY
          ? 'Time (Min)'
          : 'Time (Hrs)';

      default:
        return 'Solar Power (kW)';
    }
  }
  getTitle(): string {
    let title = '';
    switch (this.trendType) {
      case TrendType.POWER_GENERATION:
        if (this.trendSelection === TrendSelection.DAY) title = 'Time (hh:mm)';
        else if (this.trendSelection === TrendSelection.MONTHLY)
          title = 'Daily Power Generation';
        else if (this.trendSelection === TrendSelection.YEARLY)
          title = 'Monthly Power Generation';
        break;
      case TrendType.POWER_CONSUMPTION:
        if (this.trendSelection === TrendSelection.DAY) title = 'Time (hh:mm)';
        else if (this.trendSelection === TrendSelection.MONTHLY)
          title = 'Daily Power Consumption';
        else if (this.trendSelection === TrendSelection.YEARLY)
          title = 'Monthly Power Consumption';
        break;
      case TrendType.POWER_CUT:
        if (this.trendSelection === TrendSelection.DAY) title = 'Time (hh:mm)';
        else if (this.trendSelection === TrendSelection.MONTHLY)
          title = 'Daily Power Cut';
        else title = 'Monthly Power Cut';

        break;
    }
    return title;
  }
  plotLineChart(data: NoxChartDta[]) {
    
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.warn('plotLineChart: Invalid or empty data');
      this.clearGraphData();
      return;
    }
    const validData = data.filter(
      (item) =>
        item?.name && Array.isArray(item.noxXY) && item.noxXY.length > 0,
    );

    if (validData.length === 0) {
      console.warn('plotLineChart: No valid data items');
      this.clearGraphData();
      return;
    }

    this.series = validData.map((item) => {
      return {
        name: item.name,
        data: item.noxXY.map((item) => {
          return {
            x: item.x,
            y: item.y.toFixed(3),
          };
        }),
      };
    });
    /**
     * Line chart
     */
    const nonce = this.cspNonceService.getNonce() ?? undefined;

    this.chartOptions = {
      chart: {
        type: 'area',
        nonce: nonce,
        height: '96%',
        toolbar: {
          show: true,
          tools: {
            pan: true,
          },
          autoSelected: 'pan',
          export: {
            csv: {} as any,
          },
        },
        events: {
          beforeZoom: (e, { xaxis }) => {
            const noxXY = validData[0].noxXY;
            const first = noxXY.at(0);
            const last = noxXY.at(-1);
            if (!first || !last) return;
            const mainDifference =
              new Date(last.x).valueOf() - new Date(first.x).valueOf();
            const zoomDifference = xaxis.max - xaxis.min;
            if (zoomDifference > mainDifference) {
              return {
                xaxis: {
                  min: first.x,

                  max: last.x,
                },
              };
            }
            return {
              xaxis: {
                min: xaxis.min,
                max: xaxis.max,
              },
            };
          },
        },
      },
      grid: {
        padding: {
          bottom: 5,
        },
        yaxis: {
          lines: {
            show: true,
            offsetX: 5,
          },
        },
      },
      series: this.series,
      stroke: {
        width: 2,
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 0,
      },
      fill: {
        type: 'solid',
      },
      plotOptions: {},
      legend: {
        show: false,
      },
      xaxis: {
        type: 'datetime',
        tickAmount: 8,
        labels: {
          showDuplicates: false,
          datetimeUTC: false,
          formatter: function (value, timestamp: number) {
            return DateUtils.formatTimeFromLocalEpoch(timestamp);
          },
        },
        title: {
          offsetY: 4,
          text: 'Time (hh:mm)',
        },
      },
      yaxis: {
        min:0,
        title: {
          text: this.updateYAxisLabel(),
        },
        labels: {},
      },
      tooltip: {
        x: { show: true },
        y: {
          title: {
            formatter: () => ``,
          },
          formatter: (value: number) => `${value} kW`,
        },
      },
    };
    this.cdr.detectChanges();
    this.registerNonPassiveListeners();
  }
  toggleTrendDropdown() {
    this.enableTrendDropdown = !this.enableTrendDropdown;
  }

  get formattedDisplayDate(): string {
    switch (this.trendSelection) {
      case TrendSelection.DAY:
        return this.selectedDate
          ? new Intl.DateTimeFormat('en-GB').format(this.selectedDate)
          : '';
      case TrendSelection.MONTHLY:
        return this.selectedDate
          ? this.selectedDate.toLocaleString('en-US', {
              month: 'short',
              year: 'numeric',
            })
          : '';
      case TrendSelection.YEARLY:
        return this.selectedDate
          ? this.selectedDate.getFullYear().toString()
          : '';
      default:
        return '';
    }
  }
  isEnertechInverter(): boolean {
    return [
      InverterType.ENERTECH_SOLAR,
      InverterType.ENERTECH_NON_SOLAR,
      InverterType.OPTIMUS_3P
    ].includes(this.inverterType as InverterType);
  }

  isMonthYearDisabled(): boolean {
    return (
      this.isEnertechInverter() &&
      this.trendType === TrendType.POWER_CONSUMPTION
    );
  }
  changeYear(direction: number, event: Event) {
    event.stopPropagation();
    const newYear = this.noxMonthsData.year + direction;
    if (newYear < this.minYear) return;
    if (newYear > this.currentYear) return;
    this.noxMonthsData = NoxMonth.noxMonthsData(newYear);
  }
  ngOnDestroy(): void {
    this.restoreEventListeners();
    this.cleanupEventListeners();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
